// Internal modules
import { appStore } from '@/store';
import {
  CI,
  features,
  getLocale,
  parseGqlError,
  redirectToMember,
} from '@/lib';
import { apolloMutation } from '@/lib/apollo';
import { logger } from '@/lib/logger';
import {
  logGuestToMemberTransitionToFpti,
  logOnboardSuccessToFpti,
} from '../fpti';
import { threeDSHandler } from '../threeDSHandler';
import { serializeFormData } from '../utils';
import { onboardErrorHandler } from './errorHandlers';

import {
  CREATE_MEMBER_ACCOUNT_MUTATION,
  ONBOARD_GUEST_MUTATION,
  SIGN_UP_NEW_MEMBER_MUTATION,
} from './mutations';
import {
  onAfterOnboardAccountCreationHandler,
  onAfterOnboardAccountSuccessHandler,
} from './responseHandlers';
import { MULTI_PRODUCT_SUPPORTED } from '@/lib/constants';
import { clearAccessToken } from '../../../lib/globals';
import { getMutationName } from './getMutationName';
import { logAtomicsTransactionSuccessfulAndLeaveCheckout } from '../../../lib/utils/redirects/redirects';

/**
 * @function getFraudNetSyncData - Get the FraudNet data from the form
 * @returns {string | null}
 */
export const getFraudNetSyncData = () => {
  try {
    // Flush fnSync data before form submission
    if (window.PAYPAL?.syncData?.flushData) {
      window.PAYPAL.syncData.flushData();
    }
    const fnSyncDataInput = document.querySelector(
      'input[name="fn_sync_data"]',
    );

    return fnSyncDataInput && fnSyncDataInput.value
      ? fnSyncDataInput.value
      : null;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn('get_fraudnet_sync_data_error', {
      message,
    });
    return null;
  }
};

const onboardMutations = {
  createMemberAccount: CREATE_MEMBER_ACCOUNT_MUTATION,
  onboardGuest: ONBOARD_GUEST_MUTATION,
  signUpNewMember: SIGN_UP_NEW_MEMBER_MUTATION,
};

/**
 * @function onboardAction
 * @param {object} data
 * @returns {promise}
 */
const onboardAction = data => {
  const {
    api: apiState,
    form: formState,
    ui: { fiType },
  } = appStore.getState();
  const { value: onboardOption } = formState.onboardOption;
  const isExistingAccount = apiState.isExistingAccount;
  const country = getLocale().country;
  const isPOMAEligible = features.isActive('isPOMAEligible');
  const isPasswordlessForcedSignup = features.isActive(
    'passwordlessForcedSignup',
  );
  const isPOMAFlow = isPOMAEligible || isPasswordlessForcedSignup;
  const isSignup = onboardOption === 'signup';
  const accountType = isPOMAFlow && isSignup ? 'poma' : onboardOption;
  const isPayLater = fiType === 'bnpl';
  const isMultiProductSupported = MULTI_PRODUCT_SUPPORTED.includes(country);

  const mutationName = getMutationName(
    isSignup,
    isPayLater,
    isExistingAccount,
    {
      isMemberMatch:
        features.isActive('isMemberMatchNoOptIn') ||
        features.isActive('isMemberMatchNoToggleWithInterstitial'),
      isOptionalSignup: features.isActive('isOptionalSignup'),
    },
  );

  const fptiPerfLogStart = Date.now();

  logger.info('onboard_account_init', {
    accountType,
    fiType,
    mutationName,
  });

  // We can clear access token for the all transactions irrespective of
  // guest/signup flows since user is anonymous anyway whether user wants
  // to create an account/ continue as a guest user.
  clearAccessToken();

  const mutation = onboardMutations[mutationName];

  const variables = serializeFormData(data);

  // Set context for signUpNewMember mutations to include fn_sync_data
  const context = {};
  if (mutationName === 'signUpNewMember') {
    const fraudNetSyncData = getFraudNetSyncData();
    if (fraudNetSyncData) {
      context.fraudNetSyncData = fraudNetSyncData;
    }
  }

  const mutationPromise = apolloMutation({
    mutation,
    variables,
    ...(Object.keys(context).length > 0 && { context }),
  })
    .then(onAfterOnboardAccountCreationHandler)
    .then(data => {
      logOnboardSuccessToFpti(onboardOption, fptiPerfLogStart);
      return data;
    });

  if (isPayLater) {
    const fptiOptions = {
      payment_intent: 'pay_in_four',
      reason: 'signup',
      status: 'success',
    };

    return mutationPromise
      .then(() => {
        logGuestToMemberTransitionToFpti(fptiOptions);
        logAtomicsTransactionSuccessfulAndLeaveCheckout();
        // Remove modxo_redirect_reason from the redirect url to avoid redirects to
        // legacy app, and instead run the ModXO eligibility checks at xorouter level
        redirectToMember(
          {
            fromSignup: true,
            fundingSource: 'paylater',
            multiProductSupported: isMultiProductSupported,
          },
          'info',
          ['modxo_redirect_reason'],
        );
      })
      .catch(err => {
        const { message } = parseGqlError(err);

        fptiOptions.status = 'failure';
        fptiOptions.int_error_code = message;
        fptiOptions.int_error_desc = 'Error during guest-to-member transition';
        logGuestToMemberTransitionToFpti(fptiOptions);
        onboardErrorHandler(err);
      });
  }

  return mutationPromise
    .then(onAfterOnboardAccountSuccessHandler)
    .then(threeDSHandler)
    .then(() => {
      // Mark CI as success
      CI.ok();
    })
    .catch(error => {
      onboardErrorHandler(error);
      appStore.getActions().ui.toggleIsFormSubmitting(false);
    });
};

export { onboardAction };
